"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useAction, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { CheckoutShipment } from "@convex/schemas/validators/checkoutShipment";
import { useCart, type CartProductSnapshot } from "@/hooks/cart/useCart";
import type { DeliveryLocation } from "@/lib/checkout/delivery-location";
import type { DeliveryWindow } from "@/lib/checkout/delivery-window";
import { handleError } from "@/lib/handle-error";
import { isSilentlyReleasable } from "@/lib/checkout/open-checkout";
import {
  trackStartSkipped,
  useDirectCheckout,
  type DirectCheckoutRequest,
} from "./use-direct-checkout";
import type { TermsPayment } from "./use-terms-payment";
import { defaultShipments, type ShipmentProduct } from "./use-shipment-plan";

export interface ListingBuyNowInput {
  productId: Id<"products">;
  quantity: number;
  unitPriceCents: number;
  snapshot: CartProductSnapshot;
  shipmentProduct: ShipmentProduct;
  location: DeliveryLocation | null;
  deliveryWindow: DeliveryWindow;
  hasDockOnSite: boolean;
  seedShipment: () => void;
  shipments: CheckoutShipment[];
  expectedFreightCents?: number;
  expectedProductSubtotals?: Array<{
    productId: Id<"products">;
    subtotalCents: number;
  }>;
  onPriceDrift?: () => void;
  terms: TermsPayment;
}

export function buildListingShipments(
  input: Pick<
    ListingBuyNowInput,
    "productId" | "shipmentProduct" | "deliveryWindow" | "hasDockOnSite"
  > & { location: DeliveryLocation },
): CheckoutShipment[] {
  const { location } = input;
  return defaultShipments(input.shipmentProduct).map((draft) => ({
    ...draft,
    productId: input.productId,
    line1: [location.line1, location.line2].filter(Boolean).join(", "),
    city: location.city,
    state: location.state,
    postalCode: location.zip,
    deliveryDate: input.deliveryWindow.start,
    deliveryWindowEnd: input.deliveryWindow.end,
    hasDockOnSite: input.hasDockOnSite,
  }));
}

function useMemberCartPreparation(
  input: Pick<
    ListingBuyNowInput,
    "productId" | "quantity" | "snapshot" | "seedShipment"
  >,
  isGuest: boolean,
) {
  const { addToCart } = useCart();
  const openCheckout = useQuery(
    api.cart.checkoutAttemptQueries.getActiveCheckoutAttempt,
    isGuest ? "skip" : {},
  );
  const releaseOpenCheckout = useAction(
    api.cart.checkout.cancelOpenCheckoutAttempt,
  );
  const prepare = async (
    addsLineInCheckout: boolean,
  ): Promise<"ready" | "stopped" | "cart"> => {
    try {
      if (openCheckout) {
        const released = await releaseOpenCheckout({
          attemptId: openCheckout.attemptId,
        });
        if (released.otherCheckoutOpen) return "cart";
        if (released.paymentInFlight) {
          toast.info("Your last payment is still processing.");
          return "stopped";
        }
      }
      if (!addsLineInCheckout)
        await addToCart(input.productId, input.quantity, input.snapshot);
      input.seedShipment();
      return "ready";
    } catch (thrown) {
      handleError(thrown, {
        display: "banner",
        fallbackMessage: "Couldn't start checkout for this listing",
        reportIssueContext: { titlePrefix: "Buy now failed" },
      });
      return "stopped";
    }
  };
  const loading = !isGuest && openCheckout === undefined;
  const protectedCheckout =
    openCheckout != null && !isSilentlyReleasable(openCheckout);
  return { prepare, loading, protectedCheckout };
}

function listingCheckoutRequest(
  input: ListingBuyNowInput,
  onTerms: boolean,
): DirectCheckoutRequest {
  return {
    source: "listing",
    items: [
      {
        productId: input.productId,
        quantity: input.quantity,
        unitPriceCents: input.unitPriceCents,
      },
    ],
    shipments: input.shipments,
    expectedFreightCents: input.expectedFreightCents,
    expectedProductSubtotals: input.expectedProductSubtotals,
    buyNowLine: onTerms
      ? undefined
      : { productId: input.productId, quantity: input.quantity },
  };
}

export function useListingBuyNow(input: ListingBuyNowInput) {
  const { isGuest } = useCart();
  const { startCheckout, isRedirecting } = useDirectCheckout();
  const member = useMemberCartPreparation(input, isGuest);
  const router = useRouter();
  const [isPreparing, setIsPreparing] = useState(false);

  const buyNow = async (): Promise<boolean> => {
    if (!input.location || member.loading) {
      trackStartSkipped(
        "listing",
        input.location ? "checkout_state_loading" : "no_location",
      );
      return false;
    }
    if (member.protectedCheckout) {
      router.push("/cart");
      return false;
    }
    setIsPreparing(true);
    const onTerms = input.terms.onTerms;
    const prepared = isGuest ? "ready" : await member.prepare(!onTerms);
    setIsPreparing(false);
    if (prepared === "cart") router.push("/cart");
    if (prepared !== "ready") return false;
    const place = onTerms ? input.terms.place : startCheckout;
    const started = await place(listingCheckoutRequest(input, onTerms));
    if (!started) input.onPriceDrift?.();
    return started;
  };

  return {
    buyNow,
    isRedirecting: isPreparing || isRedirecting || input.terms.isPlacing,
    isLoading: member.loading,
  };
}
